import React from "react";

import { Button } from "@lattice/frontend/components/kit/Button";
import { Surface } from "@lattice/frontend/components/kit/Surface";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";

import { createWebSettingsHref } from "../web-settings-intent";
import type { T_WebSettingsAccountUpdate } from "../web-settings-machine";
import {
  SettingsPanelError,
  SettingsPanelLoading,
  SettingsPanelSuccess,
} from "./SettingsPanelState";

export type T_AccountPanelViewProps = {
  identity: string;
  onChangeEmail: () => void;
  onChangePassword: () => void;
  onSignOut: () => void;
  successMessage: string | null;
};

export function AccountPanelView({
  identity,
  onChangeEmail,
  onChangePassword,
  onSignOut,
  successMessage,
}: T_AccountPanelViewProps) {
  return (
    <div className="shell-settings-form">
      {successMessage ? (
        <SettingsPanelSuccess message={successMessage} />
      ) : null}
      <Surface tone="subtle">
        <div className="shell-settings-account__identity">
          <span>Account profile</span>
          <strong>{identity}</strong>
        </div>
        <div className="shell-settings-actions shell-settings-actions--stacked">
          <Button
            label="Change password"
            onClick={onChangePassword}
            variant="secondary"
          />
          <Button
            label="Change email"
            onClick={onChangeEmail}
            variant="secondary"
          />
          <Button label="Sign out" onClick={onSignOut} variant="quiet" />
        </div>
      </Surface>
    </div>
  );
}

export function AccountPanel({
  accountUpdate,
  logtoEndpoint,
}: {
  accountUpdate: T_WebSettingsAccountUpdate | null;
  logtoEndpoint: string;
}) {
  const currentUser = trpcApi.users.get_current_user.useQuery({});
  if (currentUser.isPending) {
    return <SettingsPanelLoading label="Loading account" />;
  }
  if (currentUser.isError || !currentUser.data) {
    return (
      <SettingsPanelError message="Account details could not be loaded." />
    );
  }

  const openAccountCenter = (path: T_WebSettingsAccountUpdate) => {
    const returnUrl = `${window.location.origin}${createWebSettingsHref("account")}`;
    window.location.href = `${logtoEndpoint}/account/${path}?redirect=${encodeURIComponent(returnUrl)}`;
  };

  return (
    <AccountPanelView
      identity={
        [currentUser.data.first_name, currentUser.data.last_name]
          .filter(Boolean)
          .join(" ") || "Lattice account"
      }
      onChangeEmail={() => openAccountCenter("email")}
      onChangePassword={() => openAccountCenter("password")}
      onSignOut={() => {
        trackEvent(MixpanelEvents.LOGOUT);
        window.location.href = "/api/auth/sign-out";
      }}
      successMessage={
        accountUpdate === "email"
          ? "Email updated successfully."
          : accountUpdate === "password"
            ? "Password updated successfully."
            : null
      }
    />
  );
}

export type T_DeleteAccountPanelViewProps = {
  deleting: boolean;
  error: string | null;
  onDelete: () => void;
};

export function DeleteAccountPanelView({
  deleting,
  error,
  onDelete,
}: T_DeleteAccountPanelViewProps) {
  return (
    <div className="shell-settings-form">
      <Surface tone="subtle">
        <p className="shell-settings-delete__title">
          Permanently delete your Lattice account?
        </p>
        <p className="shell-settings__body">
          This removes your account and saved data. This action cannot be
          undone.
        </p>
      </Surface>
      {error ? <SettingsPanelError message={error} /> : null}
      <Button
        label="Delete account forever"
        loading={deleting}
        onClick={onDelete}
        variant="danger"
      />
    </div>
  );
}

export function DeleteAccountPanel() {
  const deleteUser = trpcApi.users.delete_user.useMutation({
    onSuccess: () => {
      trackEvent(MixpanelEvents.ACCOUNT_DELETED);
      window.location.href = "/api/auth/sign-out";
    },
  });

  return (
    <DeleteAccountPanelView
      deleting={deleteUser.isPending}
      error={deleteUser.error?.message ?? null}
      onDelete={() => deleteUser.mutate()}
    />
  );
}
